import 'jquery-validation';
import { uniq, sortBy } from 'lodash-es';

/*
Overview:
  This class provides realtime validation feature.
  It totally depends on jQuery Validation. Refs: https://jqueryvalidation.org/
  And an error message for a validated field is shown at specified area.

Usage:
  - [HTML] Prepare containers to show alert in HTML. The container is needed for each field.
    - Markup a container as `div.alert-container`.
    - Markup an alert as `div.alert-for-#{attr}` under the container.
    - Markup a list as `ul` under the alert.
      This seems redundant, but there's possibility to show errors for multiple fields in one container.

    ```slim
    .alert-container.hidden
      .alert.alert-danger.alert-for-first_name.alert-for-last_name
        ul
    ```

  - [JS] Instantiate FormValidator with...
    - $form:          a form element which you want to validate
    - rules:          validation rules.      Refs: https://jqueryvalidation.org/validate/
    - messages:       validation messages.   Refs: https://jqueryvalidation.org/validate/

    ```coffee
    validator = new FormValidator($("#new_user"), rules, messages)
    validator.setValidation()
    ```
*/
type Validator = JQueryValidation.Validator & {
  pendingRequest?: number;
};
export class FormValidator {
  private form: JQuery<HTMLElement>;
  private rules: any;
  private messages: any;
  private validator: Validator | null;

  constructor($form: JQuery<HTMLElement>, rules: any, messages: any) {
    this.form = $form;
    this.rules = rules;
    this.messages = messages;
    this.validator = null;
    this.addCustomValidators();
  }

  public setValidation($selectFields: JQuery<HTMLElement> | null = null): void {
    this.validator = this.form.validate({
      rules: this.rules,
      messages: this.messages,
      wrapper: 'li',
      errorElement: 'span',
      success: (label: JQuery, element: HTMLElement) => {
        const $ul = label.closest('ul');
        if ($ul.children().length === 1) {
          $ul.closest('.alert-container').addClass('hidden');
        }
        label.closest('li').remove(); // Remove error message (li)
        return this.showFieldIcons($(element), true);
      },
      errorPlacement: (error: JQuery, element: JQuery<HTMLElement>) => {
        const name = element.attr('name');
        if (!name) return;
        const $container = $(this.convertNameToAlertSelector(name)).closest(
          '.alert-container',
        );
        $container.removeClass('hidden');
        error.appendTo(`${this.convertNameToAlertSelector(name)} ul`);
        return this.showFieldIcons(element, false);
      },
    });

    this.setValidationForSelectFields($selectFields);
    this.validateNonEmptyFields();
  }

  public pendingRequest(): number {
    return this.validator?.pendingRequest as number;
  }

  public validateElement(element: HTMLElement): boolean {
    return this.form.validate().element($(element));
  }

  private validateNonEmptyFields(): void {
    // If a field has default value or entered value before, we should validate it and show green tick.
    // Only `radio` should not be validated since it's not have tick area and couldn't handle its validity.
    this.form
      .find(':input')
      .not('[type=radio]')
      .each((_i, element) => {
        if ($(element).val()) {
          this.validateElement(element);
        }
      });
  }

  private setValidationForSelectFields(
    $selectFields: JQuery<HTMLElement> | null,
  ): void {
    // jQuery validation does not call validate method even when select element changes, so we must call it by ourselves.
    if (!$selectFields) return;
    $selectFields.on('change keyup blur', (e) => {
      this.form.validate().element($(e.target));
    });
  }

  private addCustomValidators(): void {
    const defaultMethods = Object.keys($.validator.methods);
    const usedMethods: string[] = $.map(
      this.rules,
      (obj: { [key: string]: any }) => Object.keys(obj),
    );
    const uniqueUsedMethods = uniq(usedMethods);
    const sortUsedMethods = sortBy(uniqueUsedMethods);

    const customMethods = sortUsedMethods.filter(
      (name) => !defaultMethods.includes(name),
    );
    this.addValidators(customMethods);
  }

  private addValidators(validatorNames: string[]): void {
    $.each(validatorNames, (_i: number, validatorName: string) => {
      $.validator.addMethod(
        validatorName,
        FormValidator.customValidators[validatorName],
      );
    });
  }

  private convertNameToAlertSelector(s: string): string {
    // e.g. "user[first_name]"                   => ".alert-for-first_name"
    // e.g. "user[aya_profile][first_name_kana]" => ".alert-for-aya_profile_first_name_kana"
    const r = /\[([^\]]*)\]/g;
    const a: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = r.exec(s)) !== null) {
      a.push(m[1]);
    }
    return `.alert-for-${a.join('_')}`;
  }

  private addFieldIcons($field: JQuery<HTMLElement>): void {
    if (!$field.length) return;
    const $fieldContainer = $field
      .parent('div')
      .addClass('form-control-icon-container');
    let $fieldIcon = $fieldContainer.find('.form-control-icon-right');

    if ($fieldIcon.length === 0) {
      $fieldIcon = $(
        '<label class="form-control-icon-right form-control-icon form-control-icon-info"></label>',
      ).prependTo($fieldContainer);
    }

    if ($fieldIcon.find('.icon.text-success').length === 0) {
      $fieldIcon.append(
        '<svg class="icon text-success hide"><use xlink:href="#common-tick"></use></svg>',
      );
    }

    if ($fieldIcon.find('.icon.text-danger').length === 0) {
      $fieldIcon.append(
        '<svg class="icon text-danger hide"><use xlink:href="#common-cross"></use></svg>',
      );
    }
  }

  private showFieldIcons($field: JQuery<HTMLElement>, valid: boolean): void {
    this.addFieldIcons($field);
    $field.parent().find('.icon.text-primary').addClass('hide');
    $field.parent().find('.icon.text-success').toggleClass('hide', !valid);
    $field.parent().find('.icon.text-danger').toggleClass('hide', valid);
  }

  public static ajaxValidationParams = {
    textValidation: (
      attr: string,
      tagName = 'input',
      allowAscii = false,
      allowHankakuKatakana = false,
    ) => ({
      url: '/api/users/text_validation',
      type: 'get',
      cache: false,
      async: true,
      data: {
        subject: () => $(`${tagName}[name='${attr}']`).val(),
        allow_ascii: allowAscii,
        allow_hankaku_katakana: allowHankakuKatakana,
      },
      dataFilter: (res: string) => !JSON.parse(res).disallowed_char.contains,
    }),
  };

  private static customValidators: {
    [key: string]: (value: any, element: HTMLElement, params?: any) => boolean;
  } = {
    notContainsUsername: (value, _element, params) => {
      const $username = $(params);
      const username = $username.is('input')
        ? $username.val()
        : $username.text();
      return !value || !username || !(value.indexOf(username) !== -1); // Polyfill of `includes` for IE
    },
    notContainsEmail: (value, _element, params) => {
      const $email = $(params.selector);
      const email = $email.is('input') ? $email.val() : $email.text();
      return !value || !email || !(value.indexOf(email) !== -1); // Polyfill of `includes` for IE
    },
    format: (value, _element, params) => {
      return (
        !value || params.every((regex: string) => new RegExp(regex).test(value))
      );
    },
    maxGraphemeLength: (value, _element, params) => {
      const segmenter = new Intl.Segmenter('ja', { granularity: 'grapheme' });
      return [...segmenter.segment(value)].length <= params;
    },
  };
}
